// src/components/compliance/ApprovalsTab.tsx
//
// Approval inbox — pending approval requests across the workspace,
// with approve / reject / delegate / cancel actions.
//
// APPROVER GATING (spec §6 — the load-bearing invariant):
//   - `compliance.approve` capability OR
//   - the current user is the explicit `approverUserId` on the row.
// Neither TeamRole nor ProviderRole nor a Provider Assignment ever
// grants approval authority. The backend re-enforces this; the FE
// just hides the action buttons for the caller who cannot act.

import { useMemo, useState } from 'react';
import {
  useGetComplianceDocumentsQuery,
  useListApprovalsForDocumentQuery,
  useApproveApprovalMutation,
  useCancelApprovalMutation,
  useDelegateApprovalMutation,
  useRejectApprovalMutation,
  useRequestApprovalMutation,
} from '../../api/complianceApi';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorState from '../common/ErrorState';
import Button from '../common/Button';
import { ApprovalStatusChip, DocumentStatusChip } from './Chips';
import {
  formatShortDate,
  messageFromComplianceError,
  SUBJECT_TYPE_LABEL,
} from './complianceDisplay';
import { useAuth } from '../../hooks/useAuth';
import { useHasCapability } from '../../services/centralContext';
import type { ComplianceApproval, ComplianceDocument } from '../../types/api';

interface ApprovalsTabProps {
  workspaceId: string;
}

export default function ApprovalsTab({ workspaceId: _workspaceId }: ApprovalsTabProps) {
  // The inbox === all documents currently in `pending_approval`. We
  // fetch the doc list, then fan out one approvals query per doc.
  const { data, isLoading, error, refetch } = useGetComplianceDocumentsQuery({
    status: 'pending_approval',
    limit: 200,
  });

  const [banner, setBanner] = useState<string | null>(null);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorState title="Could not load pending approvals" message={messageFromComplianceError(error)} onRetry={refetch} />;

  const rows = data?.rows ?? [];

  return (
    <section className="space-y-4" data-testid="compliance-approvals-tab">
      {banner && (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {banner}
        </div>
      )}
      {rows.length === 0 ? (
        <div className="rounded border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
          No pending approvals in this workspace.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="compliance-approvals-list">
          {rows.map((doc) => (
            <ApprovalRow key={doc.id} document={doc} onError={setBanner} />
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// ApprovalRow
// ---------------------------------------------------------------------------

function ApprovalRow({
  document,
  onError,
}: {
  document: ComplianceDocument;
  onError: (msg: string) => void;
}) {
  const { user: currentUser } = useAuth();
  const canApprove = useHasCapability('compliance', 'approve');
  const canRequest = useHasCapability('compliance', 'upload');

  const { data: approvals, isLoading } = useListApprovalsForDocumentQuery(document.id);
  const pending = useMemo(
    () => (approvals ?? []).find((a) => a.status === 'pending') ?? null,
    [approvals],
  );

  const [showReject, setShowReject] = useState(false);
  const [showDelegate, setShowDelegate] = useState(false);
  const [showRequest, setShowRequest] = useState(false);

  const [approve, approveState] = useApproveApprovalMutation();
  const [reject, rejectState] = useRejectApprovalMutation();
  const [delegate, delegateState] = useDelegateApprovalMutation();
  const [cancel, cancelState] = useCancelApprovalMutation();
  const [request, requestState] = useRequestApprovalMutation();

  const isAssignedApprover =
    !!pending && !!currentUser?.id && pending.approverUserId === currentUser.id;
  const isRequester = !!pending && !!currentUser?.id && pending.requestedByUserId === currentUser.id;
  const canAct = canApprove || isAssignedApprover;

  const busy =
    approveState.isLoading ||
    rejectState.isLoading ||
    delegateState.isLoading ||
    cancelState.isLoading ||
    requestState.isLoading;

  const handleApprove = async () => {
    if (!pending) return;
    try {
      await approve({ id: pending.id, body: {} }).unwrap();
    } catch (e) {
      onError(messageFromComplianceError(e));
    }
  };

  const handleCancel = async () => {
    if (!pending) return;
    try {
      await cancel({ id: pending.id, body: {} }).unwrap();
    } catch (e) {
      onError(messageFromComplianceError(e));
    }
  };

  return (
    <li
      className="rounded-lg border border-gray-200 bg-white p-4"
      data-testid={`compliance-approval-row-${document.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-900">{document.title}</span>
            <DocumentStatusChip status={document.status} />
            {pending && <ApprovalStatusChip status={pending.status} />}
          </div>
          <div className="mt-1 text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
            <span>Requirement: {document.requirement?.name ?? '—'}</span>
            <span>Subject: {SUBJECT_TYPE_LABEL[document.subjectType]} · <span className="font-mono">{document.subjectId}</span></span>
            <span>Requested: {formatShortDate(pending?.requestedAt)}</span>
            <span>Requested by: {pending?.requestedByUserId ?? '—'}</span>
            <span>Approver: {pending?.approverUserId ?? 'Any user with compliance.approve'}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          {!pending && canRequest && (
            <Button type="button" variant="secondary" onClick={() => setShowRequest(true)} disabled={busy} data-testid={`compliance-approval-request-${document.id}`}>
              Request approval
            </Button>
          )}
          {pending && canAct && (
            <>
              <Button type="button" onClick={handleApprove} disabled={busy} data-testid={`compliance-approval-approve-${document.id}`}>
                Approve
              </Button>
              <Button type="button" variant="secondary" onClick={() => setShowReject(true)} disabled={busy} data-testid={`compliance-approval-reject-${document.id}`}>
                Reject
              </Button>
              <Button type="button" variant="ghost" onClick={() => setShowDelegate(true)} disabled={busy} data-testid={`compliance-approval-delegate-${document.id}`}>
                Delegate
              </Button>
            </>
          )}
          {pending && (isRequester || canApprove || isAssignedApprover) && (
            <Button type="button" variant="ghost" onClick={handleCancel} disabled={busy} data-testid={`compliance-approval-cancel-${document.id}`}>
              Cancel
            </Button>
          )}
          {pending && !canAct && !isRequester && (
            <span className="text-xs text-gray-500 self-center">
              You are not authorized to act on this approval.
            </span>
          )}
        </div>
      </div>
      {isLoading && <div className="mt-3"><LoadingSpinner size="sm" /></div>}

      {showReject && pending && (
        <ReasonDialog
          title="Reject compliance evidence"
          description="A reason is required — the requester will see it."
          confirmLabel="Reject"
          onCancel={() => setShowReject(false)}
          onConfirm={async (reason) => {
            try {
              await reject({ id: pending.id, body: { reason } }).unwrap();
              setShowReject(false);
            } catch (e) {
              onError(messageFromComplianceError(e));
            }
          }}
        />
      )}

      {showDelegate && pending && (
        <DelegateDialog
          onCancel={() => setShowDelegate(false)}
          onConfirm={async (delegateeUserId, reason) => {
            try {
              await delegate({ id: pending.id, body: { delegateeUserId, reason: reason || null } }).unwrap();
              setShowDelegate(false);
            } catch (e) {
              onError(messageFromComplianceError(e));
            }
          }}
        />
      )}

      {showRequest && (
        <ReasonDialog
          title="Request approval"
          description="Optionally leave a note for the approver."
          confirmLabel="Send"
          reasonOptional
          onCancel={() => setShowRequest(false)}
          onConfirm={async (reason) => {
            try {
              await request({
                documentId: document.id,
                body: { reason: reason || null },
              }).unwrap();
              setShowRequest(false);
            } catch (e) {
              onError(messageFromComplianceError(e));
            }
          }}
        />
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Small dialogs
// ---------------------------------------------------------------------------

function ReasonDialog({
  title,
  description,
  confirmLabel,
  reasonOptional,
  onConfirm,
  onCancel,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  reasonOptional?: boolean;
  onConfirm: (reason: string) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const canSubmit = reasonOptional || reason.trim().length > 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl space-y-3" data-testid="compliance-approval-reason-dialog">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600">{description}</p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          className="w-full rounded border-gray-300 text-sm"
          placeholder={reasonOptional ? 'Optional…' : 'Required'}
          aria-label="Reason"
          data-testid="compliance-approval-reason-input"
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button
            type="button"
            onClick={async () => {
              if (!canSubmit) return;
              setBusy(true);
              try {
                await onConfirm(reason.trim());
              } finally {
                setBusy(false);
              }
            }}
            disabled={!canSubmit || busy}
            data-testid="compliance-approval-reason-confirm"
          >
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

function DelegateDialog({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: (delegateeUserId: string, reason: string) => void | Promise<void>;
}) {
  const [delegateeUserId, setDelegateeUserId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl space-y-3" data-testid="compliance-approval-delegate-dialog">
        <h3 className="text-lg font-semibold text-gray-900">Delegate approval</h3>
        <p className="text-sm text-gray-600">Hand off this approval to another user. The delegatee must be able to act on it.</p>
        <div>
          <label htmlFor="delegatee" className="block text-xs font-medium text-gray-700 mb-1">Delegatee user id</label>
          <input
            id="delegatee"
            value={delegateeUserId}
            onChange={(e) => setDelegateeUserId(e.target.value)}
            className="w-full rounded border-gray-300 text-sm font-mono"
            required
            data-testid="compliance-approval-delegatee-input"
          />
        </div>
        <div>
          <label htmlFor="delegate-reason" className="block text-xs font-medium text-gray-700 mb-1">Reason (optional)</label>
          <textarea id="delegate-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="w-full rounded border-gray-300 text-sm" />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button
            type="button"
            onClick={async () => {
              if (!delegateeUserId.trim()) return;
              setBusy(true);
              try {
                await onConfirm(delegateeUserId.trim(), reason.trim());
              } finally {
                setBusy(false);
              }
            }}
            disabled={!delegateeUserId.trim() || busy}
            data-testid="compliance-approval-delegate-confirm"
          >
            {busy ? 'Delegating…' : 'Delegate'}
          </Button>
        </div>
      </div>
    </div>
  );
}
